import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { useContext, useMemo, useState } from "react";
import { FormContext, Form } from "components/shared/Form";
import {
  useAllDealSharingLink,
  useAllSharing,
  useUpdateDealSharing,
} from "./apiSharing";
import RHFCheckbox from "components/ui/atoms/formFields/RHFCheckbox";
import FormButtons from "components/ui/atoms/FormButtons";
import DealFormHeader from "../deals/DealFormHeader";
import { useDeal } from "../deals/apiDeal";
import { UpAndDownProvider } from "components/shared/UpAndDown";
import { Box, Chip, Typography } from "@mui/material";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";

const prepareInitialValues = (sharings, deal_id) => {
  return (object) => {
    return Object.fromEntries(
      sharings
        .filter((s) => s.is_active)
        .map((s) => [
          field_name(s.id),
          {
            real_status: object?.[field_name(s.id)]?.real_status == "included",
            deal_id: deal_id,
            sharing_id: s.id,
          },
        ]),
    );
  };
};

const field_name = (sharing_id) => `sharing_${sharing_id}`;

const LinkCheckbox = ({ sharing, sharingLink, name }) => {
  const {
    form: { watch },
  } = useContext(FormContext);

  const link = watch(name);

  const forced =
    (link.real_status && sharingLink?.auto_status != "included") ||
    (!link.real_status && sharingLink?.auto_status == "included");

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography noWrap>{sharing.name}</Typography>
      <Box
        sx={{
          ml: "auto",
          mr: 1,
          flexShrink: 0,
          flexGrow: 0,
        }}
      >
        {forced && <Chip label="forced" />}
      </Box>
      <Box sx={{ flexShrink: 0, flexGrow: 0 }}>
        <RHFCheckbox name={`${name}.real_status`} />
        <RHFTextField type="hidden" name={`${name}.deal_id`} />
        <RHFTextField type="hidden" name={`${name}.sharing_id`} />
      </Box>
    </Box>
  );
};

const DealSharingLinksFormFields = ({
  sharings,
  sharingLinks,
  deal,
  onCancel,
}) => {
  const {
    form: { reset },
    prepareInitialValues,
  } = useContext(FormContext);

  const sharingLinksDico = useMemo(() => {
    return Object.fromEntries(sharingLinks.map((x) => [x.sharing_id, x]));
  }, [sharingLinks]);

  const clear = () => {
    reset(prepareInitialValues({}));
  };

  const resetForm = () => {
    reset(
      prepareInitialValues(
        Object.fromEntries(
          sharingLinks.map((x) => [
            field_name(x.sharing_id),
            { ...x, real_status: x.auto_status },
          ]),
        ),
      ),
    );
  };

  return (
    <>
      <DealFormHeader formType="update" onClose={onCancel} deal={deal} />
      <Grid container columnSpacing={2} pb={10} pr={2} pl={2} pt={"70px"}>
        {sharings.map((s) => (
          <Grid size={6} key={s.id}>
            <LinkCheckbox
              sharing={s}
              sharingLink={sharingLinksDico[s.id]}
              name={field_name(s.id)}
            />
          </Grid>
        ))}
      </Grid>
      <FormButtons>
        <Button variant="outlined" onClick={resetForm} sx={{ mr: 1 }}>
          Reset
        </Button>
        <Button variant="outlined" onClick={clear}>
          Clear
        </Button>
      </FormButtons>
    </>
  );
};

const DealSharingLinksForm = ({
  objectId: initialObjectId,
  onCancel,
  onUpdated,
  values: upAndDownValues,
  ...props
}) => {
  const [objectId, setObjectId] = useState(initialObjectId);
  const { data: sharings } = useAllSharing();
  const { data: deal } = useDeal(objectId);
  const { data: sharingLinks } = useAllDealSharingLink({
    deal: objectId,
  });
  const { mutateAsync } = useUpdateDealSharing();

  const formValues = useMemo(() => {
    if (sharingLinks)
      return Object.fromEntries(
        sharingLinks.map((x) => [field_name(x.sharing_id), x]),
      );
    return {};
  }, [sharingLinks]);

  const handleSubmit = async (values) => {
    await mutateAsync({
      links: Object.keys(values).map(function (key) {
        return values[key];
      }),
    });
    onUpdated && (await onUpdated());
  };

  if (!sharings) return <></>;
  if (!sharingLinks) return <></>;
  if (!formValues) return <></>;
  if (!deal) return <></>;

  return (
    <UpAndDownProvider values={upAndDownValues} onChange={setObjectId}>
      <Form
        key={objectId}
        prepareInitialValues={prepareInitialValues(sharings, objectId)}
        initialValues={formValues}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        {...props}
      >
        <DealSharingLinksFormFields
          sharings={sharings.filter((s) => s.is_active)}
          sharingLinks={sharingLinks || []}
          deal={deal}
          onCancel={onCancel}
        />
      </Form>
    </UpAndDownProvider>
  );
};

export default DealSharingLinksForm;
